import React from "react";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import {
  useTrackColumnSizing,
  useStickyEdgeShadow,
  TrackMatrixHeaderRow,
  STICKY_COL_WIDTH,
} from "./trackMatrixShared";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";

const SORT_OPTIONS = [
  { value: "completion", label: "Avg completion" },
  { value: "name", label: "Name (A–Z)" },
  { value: "members", label: "Team members" },
];

// Heat tint for a completion %, low (red) -> high (green), mirroring the
// reference design. `null` (nobody assigned) is rendered separately as a muted
// dash, not via this scale.
const getCompletionHeatClass = (pct) => {
  if (pct >= 90) return "bg-emerald-600 text-white";
  if (pct >= 75) return "bg-emerald-400 text-emerald-900";
  if (pct >= 50) return "bg-emerald-200 text-emerald-900";
  if (pct >= 25) return "bg-amber-100 text-amber-800";
  return "bg-rose-100 text-rose-700";
};

const HEAT_LEGEND = [
  { label: "0–24%", className: "bg-rose-100 border-rose-200" },
  { label: "25–49%", className: "bg-amber-100 border-amber-200" },
  { label: "50–74%", className: "bg-emerald-200 border-emerald-300" },
  { label: "75–89%", className: "bg-emerald-400 border-emerald-500" },
  { label: "90–100%", className: "bg-emerald-600 border-emerald-600" },
];

// Section-weighted completion % for one (location, track) entry. Falls back to
// the people-completed ratio for the rare zero-section track, and null when
// nobody at the location is assigned the track.
const entryCompletionPct = (entry) => {
  if (!entry) return null;
  if (entry.total_sections > 0) {
    return Math.round((entry.sections_completed / entry.total_sections) * 100);
  }
  if (entry.assigned_count > 0) {
    return Math.round((entry.completed_count / entry.assigned_count) * 100);
  }
  return null;
};

function LocationTrackMatrix(props) {
  const stores = props.stores || [];
  const publishedTracks = props.publishedTracks || [];

  const [searchInput, setSearchInput] = React.useState("");
  const [sortBy, setSortBy] = React.useState("completion");
  const [viewMenuAnchor, setViewMenuAnchor] = React.useState(null);
  const [trackFilter, setTrackFilter] = React.useState([]); // track uuids to show ([] = all)
  const [filterMenuAnchor, setFilterMenuAnchor] = React.useState(null);

  const toggleTrackFilter = (uuid) =>
    setTrackFilter((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    );

  // Columns = every published track for the company (so a published track
  // shows even with nobody enrolled), unioned with any track that has an
  // enrollment, which also carries the assigned counts. Draft tracks are
  // already excluded server-side from both lists.
  const trackColumns = React.useMemo(() => {
    const map = new Map();
    (publishedTracks || []).forEach((t) => {
      if (!t?.track_uuid) return;
      map.set(t.track_uuid, {
        uuid: t.track_uuid,
        name: t.track_name || "Untitled track",
        cover: t.cover || null,
        assignment_type: t.assignment_type || "track",
        assignedCount: 0,
      });
    });
    stores.forEach((store) => {
      (store?.learning_track_enrollment_summary_by_track || []).forEach((t) => {
        if (!t?.track_uuid) return;
        const existing = map.get(t.track_uuid);
        if (existing) {
          existing.assignedCount += t.assigned_count || 0;
        } else {
          map.set(t.track_uuid, {
            uuid: t.track_uuid,
            name: t.track_name || "Untitled track",
            cover: t.cover || null,
            assignment_type: t.assignment_type || "track",
            assignedCount: t.assigned_count || 0,
          });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [stores, publishedTracks]);

  // Track columns actually shown (track filter hides the rest; [] = all).
  const visibleTrackColumns = React.useMemo(() => {
    if (!trackFilter.length) return trackColumns;
    const keep = new Set(trackFilter);
    return trackColumns.filter((t) => keep.has(t.uuid));
  }, [trackColumns, trackFilter]);

  // Per-store lookup: track_uuid -> aggregate entry, plus the store's overall
  // completion (for the "Avg completion" sort).
  const storeRows = React.useMemo(() => {
    return stores.map((store) => {
      const perTrack = new Map();
      (store?.learning_track_enrollment_summary_by_track || []).forEach((t) => {
        if (!t?.track_uuid) return;
        perTrack.set(t.track_uuid, {
          sections_completed: t.sections_completed || 0,
          total_sections: t.total_sections || 0,
          assigned_count: t.assigned_count || 0,
          completed_count: t.completed_count || 0,
        });
      });
      const summary = store?.learning_track_enrollment_summary || {};
      const den = summary.total_sections_denomenator || 0;
      const overallPct =
        den > 0
          ? Math.round((summary.total_sections_numerator / den) * 100)
          : 0;
      return {
        id: store.id,
        name: store.store_name || "Location",
        memberCount: store.team_member_count || 0,
        perTrack,
        overallPct,
        store,
        isAggregate: false,
      };
    });
  }, [stores]);

  // "All locations" aggregate row: sum numerators/denominators per track across
  // every store (store-scoped, consistent with the rest of the page).
  const aggregateRow = React.useMemo(() => {
    const perTrack = new Map();
    let memberCount = 0;
    storeRows.forEach((row) => {
      memberCount += row.memberCount;
      row.perTrack.forEach((entry, trackUuid) => {
        const acc = perTrack.get(trackUuid) || {
          sections_completed: 0,
          total_sections: 0,
          assigned_count: 0,
          completed_count: 0,
        };
        acc.sections_completed += entry.sections_completed;
        acc.total_sections += entry.total_sections;
        acc.assigned_count += entry.assigned_count;
        acc.completed_count += entry.completed_count;
        perTrack.set(trackUuid, acc);
      });
    });
    return {
      id: "__all__",
      name: "All locations",
      memberCount,
      perTrack,
      isAggregate: true,
    };
  }, [storeRows]);

  const sortedStoreRows = React.useMemo(() => {
    const normalizedSearch = searchInput.trim().toLowerCase();
    const filtered = normalizedSearch
      ? storeRows.filter((row) =>
          row.name.toLowerCase().includes(normalizedSearch)
        )
      : storeRows;
    const byName = (a, b) => a.name.localeCompare(b.name);
    return [...filtered].sort((a, b) => {
      if (sortBy === "name") return byName(a, b);
      if (sortBy === "members") {
        return b.memberCount - a.memberCount || byName(a, b);
      }
      // completion
      return b.overallPct - a.overallPct || byName(a, b);
    });
  }, [storeRows, searchInput, sortBy]);

  // Aggregate row is always pinned on top; store rows follow the active sort.
  const displayRows = React.useMemo(
    () => [aggregateRow, ...sortedStoreRows],
    [aggregateRow, sortedStoreRows]
  );

  const { matrixScrollRef, trackColumnWidth, useHorizontalHeader } =
    useTrackColumnSizing(visibleTrackColumns.length);
  const { handleMatrixScroll, renderStickyEdgeShadow } = useStickyEdgeShadow();

  const exportCSV = () => {
    const rows = displayRows.map((row) => {
      const out = { Location: row.name, "Team Members": row.memberCount };
      visibleTrackColumns.forEach((col) => {
        const pct = entryCompletionPct(row.perTrack.get(col.uuid));
        out[col.name] = pct === null ? "" : `${pct}%`;
      });
      return out;
    });
    if (rows.length === 0) return;
    downloadCSV(objectToCSV(rows), "assignment_completion.csv");
  };

  const renderCell = (row, track) => {
    const entry = row.perTrack.get(track.uuid);
    const pct = entryCompletionPct(entry);
    return (
      <td
        key={`${row.id}-${track.uuid}`}
        className="border-l border-slate-100 px-1.5 py-1.5 text-center align-middle"
        style={{ width: trackColumnWidth, minWidth: trackColumnWidth }}
      >
        {pct === null ? (
          <div className="mx-auto flex h-9 items-center justify-center rounded-md bg-slate-50 text-xs text-slate-300">
            —
          </div>
        ) : (
          <Tooltip
            arrow
            title={
              <div>
                <div className="font-semibold">{track.name}</div>
                <div>{row.name}</div>
                <div>{pct}% complete</div>
                <div>
                  {entry.sections_completed} of {entry.total_sections} sections
                  completed
                </div>
                <div>
                  {entry.completed_count} of {entry.assigned_count} people complete
                </div>
              </div>
            }
          >
            <div
              className={`mx-auto flex h-9 items-center justify-center rounded-md text-[10px] font-semibold ${getCompletionHeatClass(
                pct
              )}`}
            >
              {pct}%
            </div>
          </Tooltip>
        )}
      </td>
    );
  };

  const renderLocationCell = (row) => {
    const interactive = !row.isAggregate && typeof props.onSelectStore === "function";
    const bgClass = row.isAggregate
      ? "bg-slate-50 group-hover:bg-slate-50"
      : "bg-white group-hover:bg-slate-50";
    return (
      <td className={`sticky left-0 z-10 py-2 align-middle ${bgClass}`}>
        {renderStickyEdgeShadow()}
        <button
          type="button"
          disabled={!interactive}
          onClick={() => interactive && props.onSelectStore(row.store)}
          className={`flex items-center gap-3 px-5 text-left ${
            interactive ? "cursor-pointer" : "cursor-default"
          }`}
          style={{ width: STICKY_COL_WIDTH }}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100">
            <LocationOnIcon
              className="text-violet-600"
              style={{ fontSize: "20px" }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className={`truncate text-sm ${
                row.isAggregate
                  ? "font-bold text-slate-900"
                  : "font-semibold text-slate-900"
              }`}
            >
              {row.name}
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <PeopleIcon style={{ fontSize: "14px" }} />
              {row.memberCount}
            </div>
          </div>
          {interactive && (
            <ChevronRightIcon
              className="shrink-0 text-slate-300"
              style={{ fontSize: "20px" }}
            />
          )}
        </button>
      </td>
    );
  };

  const renderRadioMenuItem = (value, label) => {
    const active = sortBy === value;
    return (
      <MenuItem
        key={value}
        onClick={() => {
          setSortBy(value);
          setViewMenuAnchor(null);
        }}
        sx={{ py: 0.75 }}
      >
        <ListItemText
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: active ? 600 : 400,
            color: active ? "#6d28d9" : "#334155",
          }}
        >
          {label}
        </ListItemText>
        {active && <CheckIcon sx={{ color: "#6d28d9", fontSize: 18, ml: 1 }} />}
      </MenuItem>
    );
  };

  return (
    <div className="px-6 py-8">
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={(e) => setViewMenuAnchor(e.currentTarget)}
            className={`flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium transition-colors hover:border-slate-300 hover:bg-slate-50 ${
              sortBy !== "completion" ? "text-violet-700" : "text-slate-800"
            }`}
          >
            <ViewListOutlinedIcon style={{ fontSize: "18px" }} />
            View
            <KeyboardArrowDownIcon
              className="text-slate-500"
              style={{ fontSize: "18px" }}
            />
          </button>
          <button
            type="button"
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            className={`flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium transition-colors hover:border-slate-300 hover:bg-slate-50 ${
              trackFilter.length > 0 ? "text-violet-700" : "text-slate-800"
            }`}
          >
            <FilterAltOutlinedIcon style={{ fontSize: "18px" }} />
            Filter
            {trackFilter.length > 0 && (
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold text-violet-700">
                {trackFilter.length}
              </span>
            )}
            <KeyboardArrowDownIcon
              className="text-slate-500"
              style={{ fontSize: "18px" }}
            />
          </button>
          <div className="w-[240px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-slate-400"
                style={{ fontSize: "20px" }}
              />
              <input
                className="h-[46px] w-full rounded-lg border border-slate-200 pl-10 pr-4 text-sm outline-none transition-all duration-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                type="text"
                placeholder="Search locations..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>
          </div>
        </div>
        <button
          className="flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg bg-emerald-600 px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
          onClick={exportCSV}
        >
          <DownloadIcon style={{ fontSize: "18px" }} />
          Export CSV
        </button>
      </div>

      {/* View menu: sorting */}
      <Menu
        anchorEl={viewMenuAnchor}
        open={Boolean(viewMenuAnchor)}
        onClose={() => setViewMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { minWidth: 220, mt: 1 } }}
      >
        <div className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Sort by
        </div>
        {SORT_OPTIONS.map((opt) => renderRadioMenuItem(opt.value, opt.label))}
      </Menu>

      {/* Filter menu: tracks */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { minWidth: 260, maxHeight: 460, mt: 1 } }}
        MenuListProps={{ sx: { pt: 0 } }}
      >
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[11px] font-semibold uppercase leading-5 tracking-wide text-slate-400">
            Tracks
          </span>
          {trackFilter.length > 0 && (
            <button
              type="button"
              className="text-xs font-medium leading-5 text-violet-700 hover:text-violet-800"
              onClick={() => setTrackFilter([])}
            >
              Clear all
            </button>
          )}
        </div>
        <Divider />
        {trackColumns.map((track) => (
          <MenuItem
            key={track.uuid}
            onClick={() => toggleTrackFilter(track.uuid)}
            sx={{ py: 0.25 }}
          >
            <Checkbox
              checked={trackFilter.includes(track.uuid)}
              size="small"
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                py: 0.5,
              }}
            />
            <ListItemText
              primaryTypographyProps={{ fontSize: 14, color: "#334155" }}
            >
              {track.name}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <div className="-mx-6 border-y border-slate-200 bg-white">
        {/* Banner + heat legend */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Assignment completion
            </p>
            <p className="text-xs text-slate-500">
              {visibleTrackColumns.length} tracks across {storeRows.length}{" "}
              locations
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-xxs font-medium uppercase tracking-wide text-slate-400">
              Lower
            </span>
            {HEAT_LEGEND.map((bucket) => (
              <div
                key={bucket.label}
                className="flex items-center gap-1.5 text-xs text-slate-600"
              >
                <span
                  className={`h-3 w-3 rounded-sm border ${bucket.className}`}
                />
                <span>{bucket.label}</span>
              </div>
            ))}
            <span className="text-xxs font-medium uppercase tracking-wide text-slate-400">
              Higher
            </span>
          </div>
        </div>

        {storeRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
            <InfoIcon
              className="mb-3 text-slate-400"
              style={{ fontSize: "28px" }}
            />
            <p className="text-sm font-medium text-slate-900">
              No locations to show yet
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Assignment completion will appear here once your locations have
              team members.
            </p>
          </div>
        ) : (
          <>
            {/* No tracks assigned anywhere yet: keep the location list clickable
                so a manager can still drill into a store and assign the first
                track. Hiding the rows here is a dead end — the "Assign track"
                action lives one level down, inside each location. */}
            {trackColumns.length === 0 && (
              <div className="flex items-start gap-2.5 border-b border-slate-200 bg-violet-50 px-5 py-3">
                <InfoIcon
                  className="mt-0.5 shrink-0 text-violet-500"
                  style={{ fontSize: "18px" }}
                />
                <p className="text-sm text-slate-600">
                  No training tracks assigned yet. Select a location below to
                  assign a track to its team members.
                </p>
              </div>
            )}
            <div
              ref={matrixScrollRef}
              className="overflow-x-auto"
              onScroll={handleMatrixScroll}
            >
              <table className="w-full table-fixed divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <TrackMatrixHeaderRow
                    columns={visibleTrackColumns}
                    trackColumnWidth={trackColumnWidth}
                    useHorizontalHeader={useHorizontalHeader}
                    stickyLabel="Location"
                    renderStickyEdgeShadow={renderStickyEdgeShadow}
                    renderColumnMeta={(track) =>
                      `${track.assignedCount} assigned`
                    }
                  />
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {displayRows.map((row) => (
                    <tr key={row.id} className="group hover:bg-slate-50">
                      {renderLocationCell(row)}
                      {visibleTrackColumns.map((track) =>
                        renderCell(row, track)
                      )}
                      <td
                        aria-hidden="true"
                        className="border-l border-slate-100"
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LocationTrackMatrix;
